import { request } from '../request';
import { useAuthStore } from '@/stores/authStore';

function clearAuthTokens() {
  localStorage.removeItem('access_token');
  localStorage.removeItem('token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('refreshToken');
}

function handleUnauthorized() {
  clearAuthTokens();

  try {
    useAuthStore.getState().logout();
  } catch {
    // 忽略可能存在的依赖初始化异常
  }
}

/**
 * 获取精品资源
 * GET /learning/featured
 *
 * @returns 推荐的精品学习资源列表
 */
export function fetchFeaturedLearningResources() {
  return request<Api.Learning.ResourceResponse[]>({
    url: '/learning/featured',
    method: 'get'
  });
}

/**
 * 获取我的收藏资源
 * GET /learning/me/favorites
 *
 * @param params 筛选与分页参数 (包含 category_id, resource_type, page, page_size 等)
 * @returns 收藏资源的分页列表
 */
export function fetchMyFavoriteLearningResources(params?: Api.Learning.GetLearningResourcesParams) {
  return request<Api.Learning.ResourceListResponse>({
    url: '/learning/me/favorites',
    method: 'get',
    params
  });
}

/**
 * 获取学习资源分类
 * GET /learning/resource-categories
 *
 * @returns 学习资源分类树/列表
 */
export function fetchLearningResourceCategories() {
  return request<Api.Learning.CategoryResponse[]>({
    url: '/learning/resource-categories',
    method: 'get'
  });
}

/**
 * 查询已发布学习资源
 * GET /learning/resources
 *
 * @param params 筛选与分页参数 (包含 category_id, resource_type, difficulty, keyword, access_mode, featured, sort, page, page_size)
 * @returns 已发布资源的分页列表
 */
export function fetchLearningResources(params?: Api.Learning.GetLearningResourcesParams) {
  return request<Api.Learning.ResourceListResponse>({
    url: '/learning/resources',
    method: 'get',
    params
  });
}

/**
 * 获取学习资源详情
 * GET /learning/resources/{id}
 *
 * @param id 资源 ID 标识
 * @returns 包含资源元数据、课时目录、关联链接、进度等信息的详情
 */
export function fetchLearningResourceDetail(id: string | number) {
  return request<Api.Learning.ResourceDetailResponse>({
    url: `/learning/resources/${id}`,
    method: 'get'
  });
}

/**
 * 收藏学习资源
 * POST /learning/resources/{id}/favorite
 *
 * @param id 资源 ID 标识
 * @returns 收藏结果状态
 */
export function favoriteLearningResource(id: string | number) {
  return request<Api.Learning.FavoriteResponse>({
    url: `/learning/resources/${id}/favorite`,
    method: 'post'
  });
}

/**
 * 取消收藏学习资源
 * DELETE /learning/resources/{id}/favorite
 *
 * @param id 资源 ID 标识
 * @returns 操作结果
 */
export function unfavoriteLearningResource(id: string | number) {
  return request<Api.Learning.UnfavoriteResponse>({
    url: `/learning/resources/${id}/favorite`,
    method: 'delete'
  });
}

/**
 * 获取资源推荐
 * GET /learning/resources/{id}/recommendations
 *
 * @param id 资源 ID 标识
 * @returns 相关推荐学习资源列表
 */
export function fetchLearningResourceRecommendations(id: string | number) {
  return request<Api.Learning.ResourceResponse[]>({
    url: `/learning/resources/${id}/recommendations`,
    method: 'get'
  });
}

/**
 * 续期学习资源富文本图片地址
 * POST /learning/resources/{id}/asset-access
 *
 * @param id 资源 ID 标识
 * @param dataOrAssetIds 资产 ID 数组或 Api.Learning.ResourceAssetAccessRequest 请求对象
 */
export function fetchResourceAssetAccess(
  id: string | number,
  dataOrAssetIds: string[] | Api.Learning.ResourceAssetAccessRequest
) {
  const data = Array.isArray(dataOrAssetIds) ? { asset_ids: dataOrAssetIds } : dataOrAssetIds;

  return request<Api.Learning.ResourceAssetAccessResponse>({
    url: `/learning/resources/${id}/asset-access`,
    method: 'post',
    data
  });
}



/**
 * 获取学习资源目录
 * GET /learning/resources/{id}/catalog
 *
 * @param id 资源 ID 标识
 * @returns 资源对应的课时/章节目录结构
 */
export function fetchLearningResourceCatalog(id: string | number) {
  return request<Api.Learning.ResourceCatalogResponse>({
    url: `/learning/resources/${id}/catalog`,
    method: 'get'
  });
}

/**
 * 申请或续签富文本嵌入视频播放授权
 * POST /learning/embedded-videos/{id}/play-auth
 *
 * @param id 嵌入视频 ID 标识
 * @returns 包含播放 Manifest 及播放会话 ID 的授权响应
 */
export function requestEmbeddedVideoPlayAuth(id: string | number) {
  return request<Api.Learning.PlayManifestResponse>({
    url: `/learning/embedded-videos/${id}/play-auth`,
    method: 'post'
  });
}

/**
 * 播放文章嵌入视频
 * GET /learning/embedded-videos/{id}/manifest
 *
 * @param id 嵌入视频 ID 标识
 * @returns 视频播放 Manifest 描述及播放会话标识
 */
export function fetchEmbeddedVideoManifest(id: string | number) {
  return request<Api.Learning.PlayManifestResponse>({
    url: `/learning/embedded-videos/${id}/manifest`,
    method: 'get'
  });
}


/**
 * 获取嵌入视频的 Manifest 播放清单字符串
 *
 * @param id 嵌入视频 ID 标识
 * @returns m3u8 播放清单文本
 */
export async function fetchEmbeddedVideoManifestData(id: string | number): Promise<string> {
  const baseURL = import.meta.env.VITE_SERVICE_BASE_URL || '';
  const url = `${baseURL.replace(/\/$/, '')}/learning/embedded-videos/${id}/manifest`;
  const token = localStorage.getItem('access_token') || localStorage.getItem('token');
  const reqHeaders: Record<string, string> = {};
  if (token) reqHeaders.Authorization = `Bearer ${token}`;

  const res = await fetch(url, { headers: reqHeaders });
  const raw = await res.text();
  if (!res.ok) throw new Error(raw || `HTTP Error ${res.status}`);

  let manifest = '';
  try {
    const envelope = JSON.parse(raw);
    manifest = envelope.data?.manifest || envelope.manifest || '';
  } catch {
    manifest = raw;
  }
  if (!manifest) throw new Error('嵌入视频播放清单为空');
  return manifest;
}

/* ==========================================
 * 前台/学习资源访问 API (学习访问.md)
 * ========================================== */

/**
 * 访问学习课时
 * POST /learning/lessons/{id}/access
 *
 * @param id 课时 ID 标识
 * @returns 包含访问来源、文章内容、富文本资产及关联链接等信息的响应
 */
export async function accessLesson(id: string | number): Promise<{
  code?: number;
  message?: string;
  msg?: string;
  error_key?: string;
  data?: Api.Learning.LessonAccessResponse;
}> {
  const baseURL = import.meta.env.VITE_SERVICE_BASE_URL || '';
  const url = `${baseURL.replace(/\/$/, '')}/learning/lessons/${id}/access`;
  const token = localStorage.getItem('access_token') || localStorage.getItem('token');
  const reqHeaders: Record<string, string> = {
    'Content-Type': 'application/json'
  };

  if (token) {
    reqHeaders.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    method: 'POST',
    headers: reqHeaders
  });

  let resData: {
    code?: number;
    message?: string;
    msg?: string;
    error_key?: string;
    data?: Api.Learning.LessonAccessResponse;
  } | null = null;

  try {
    resData = await response.json();
  } catch {
    resData = null;
  }

  if (
    response.status === 401 ||
    resData?.code === 40001 ||
    resData?.error_key === 'http.unauthorized'
  ) {
    if (!url.includes('/accounts/sessions/logout')) {
      handleUnauthorized();
    }
    throw new Error(resData?.message || resData?.msg || '未登录或登录已过期');
  }

  if (!response.ok) {
    throw new Error(resData?.message || resData?.msg || `HTTP Error ${response.status}`);
  }

  return resData || {};
}

/**
 * 获取学习视频 HLS 播放清单
 * GET /learning/lessons/{id}/manifest
 *
 * @param id 课时 ID 标识
 * @param headers 请求头扩展字段：X-Device-ID（播放设备标识）与 X-Play-Session-ID（已有播放会话 ID）
 * @returns UTF-8 HLS 播放清单字符串
 */
export function fetchLessonManifest(
  id: string | number,
  headers?: { 'X-Device-ID'?: string; 'X-Play-Session-ID'?: string }
) {
  return request<string>({
    url: `/learning/lessons/${id}/manifest`,
    method: 'get',
    headers: headers as Record<string, string>
  });
}

/**
 * 带会话 Header 的学习视频 Manifest 提取函数
 *
 * @param id 课时 ID 标识
 * @param sessionId 已有的播放会话 ID（可选）
 * @returns 包含 manifest 文本与更新后的 sessionId
 */
export async function fetchLessonManifestWithSession(
  id: string | number,
  sessionId?: string
): Promise<{ manifest: string; sessionId?: string }> {
  const baseURL = import.meta.env.VITE_SERVICE_BASE_URL || '';
  const url = `${baseURL.replace(/\/$/, '')}/learning/lessons/${id}/manifest`;
  const token = localStorage.getItem('access_token') || localStorage.getItem('token');
  const reqHeaders: Record<string, string> = {};
  if (token) reqHeaders.Authorization = `Bearer ${token}`;
  if (sessionId) reqHeaders['X-Play-Session-ID'] = sessionId;

  const res = await fetch(url, { headers: reqHeaders });
  const manifest = await res.text();

  // 检查响应是否为 JSON 格式的错误返回（如 code: 52003, error_key: "learning.resource_locked"）
  try {
    const parsed = JSON.parse(manifest);
    if (parsed && typeof parsed === 'object') {
      if (parsed.code !== undefined && parsed.code !== 0 && parsed.code !== 200) {
        const errMsg = parsed.message || parsed.msg || '该学习资源需要购买积分解锁后访问';
        const err = new Error(errMsg);
        (err as any).code = parsed.code;
        (err as any).errorKey = parsed.error_key;
        throw err;
      }
    }
  } catch (e: any) {
    if (e.code !== undefined || e.errorKey) {
      throw e;
    }
  }

  if (!res.ok) {
    let errMsg = manifest;
    try {
      const parsed = JSON.parse(manifest);
      errMsg = parsed.message || parsed.msg || manifest;
    } catch {}
    throw new Error(errMsg || `HTTP Error ${res.status}`);
  }

  const newSessionId = res.headers.get('X-Play-Session-ID') || sessionId;
  return { manifest, sessionId: newSessionId || undefined };
}

/**
 * 申请学习视频短期播放授权
 * POST /learning/lessons/{id}/play-auth
 *
 * @param id 课时 ID 标识
 * @param headers 请求头扩展字段：X-Device-ID（播放设备标识）
 * @returns 包含播放 Manifest 及播放会话 ID 的授权响应
 */
export function requestLessonPlayAuth(
  id: string | number,
  headers?: { 'X-Device-ID'?: string }
) {
  return request<Api.Learning.PlayManifestResponse>({
    url: `/learning/lessons/${id}/play-auth`,
    method: 'post',
    headers: headers as Record<string, string>
  });
}

/**
 * 结束视频播放会话
 * POST /learning/play-sessions/{session_id}/end
 *
 * @param sessionId 播放会话 ID 标识
 * @returns 操作响应结果
 */
export function endPlaySession(sessionId: string) {
  return request<Record<string, any>>({
    url: `/learning/play-sessions/${sessionId}/end`,
    method: 'post'
  });
}

/**
 * 更新视频播放会话 (心跳)
 * POST /learning/play-sessions/{session_id}/heartbeat
 *
 * @param sessionId 播放会话 ID 标识
 * @returns 操作响应结果
 */
export function heartbeatPlaySession(sessionId: string) {
  return request<Record<string, any>>({
    url: `/learning/play-sessions/${sessionId}/heartbeat`,
    method: 'post'
  });
}

/**
 * 查询学习资源权益
 * GET /learning/resources/{id}/entitlement
 *
 * @param id 资源 ID 标识
 * @returns 资源权益响应信息
 */
export function fetchResourceEntitlement(id: string | number) {
  return request<Record<string, any>>({
    url: `/learning/resources/${id}/entitlement`,
    method: 'get'
  });
}

/* ==========================================
 * 前台/学习资源学习记录 API (学习记录.md)
 * ========================================== */

/**
 * 上报学习进度
 * POST /learning/lessons/{id}/progress
 *
 * @param id 课时 ID 标识
 * @param data 进度上报数据 (包含 progress_percent 必填项，以及 last_position_seconds, play_session_id, progress_seconds)
 * @returns 更新后的学习进度响应
 */
export function reportLessonProgress(id: string | number, data: Api.Learning.ProgressRequest) {
  return request<Api.Learning.ProgressResponse>({
    url: `/learning/lessons/${id}/progress`,
    method: 'post',
    data
  });
}

/**
 * 获取我的学习资源访问历史
 * GET /learning/me/access-history
 *
 * @param params 分页参数 (page, page_size)
 * @returns 我的学习资源访问历史分页列表
 */
export function fetchMyAccessHistory(params?: Api.Learning.PaginationParams) {
  return request<Api.Learning.AccessHistoryListResponse>({
    url: '/learning/me/access-history',
    method: 'get',
    params
  });
}

/**
 * 获取我的学习资源
 * GET /learning/me/resources
 *
 * @param params 分页参数 (page, page_size)
 * @returns 我的学习资源分页列表
 */
export function fetchMyLearningResources(params?: Api.Learning.PaginationParams) {
  return request<Api.Learning.ResourceListResponse>({
    url: '/learning/me/resources',
    method: 'get',
    params
  });
}

/**
 * 获取资源学习进度
 * GET /learning/resources/{id}/progress
 *
 * @param id 资源 ID 标识
 * @returns 资源下各课时的学习进度列表
 */
export function fetchResourceProgress(id: string | number) {
  return request<Api.Learning.ProgressResponse[]>({
    url: `/learning/resources/${id}/progress`,
    method: 'get'
  });
}

/**
 * 获取我已解锁的学习资源
 * GET /learning/me/unlocked-resources
 *
 * @param params 分页参数 (page, page_size)
 * @returns 我已解锁的学习资源分页列表
 */
export function fetchMyUnlockedResources(params?: Api.Learning.PaginationParams) {
  return request<Api.Learning.UnlockedResourceListResponse>({
    url: '/learning/me/unlocked-resources',
    method: 'get',
    params
  });
}

/**
 * 使用积分解锁学习内容
 * POST /learning/resources/{id}/unlock
 *
 * @param id 学习资源 ID
 * @param data 请求 body (idempotency_key)
 * @returns 解锁结果状态
 */
export function unlockLearningContent(id: string | number, data?: Api.Membership.UnlockContentRequest) {
  return request<Api.Membership.UnlockContentResponse>({
    url: `/learning/resources/${id}/unlock`,
    method: 'post',
    data
  });
}

